import { ChevronRight } from "lucide-react";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { useProfileCompletion } from "@/pages/candidate-profile/hooks/useCandidateProfile";

interface ProfileCompletionCardProps {
  profileId?: number | string;
  showViewDetails?: boolean;
  title?: string;
  className?: string;
}

export function ProfileCompletionCard({
  profileId,
  showViewDetails = true,
  title = "Profile Completion",
  className = "",
}: ProfileCompletionCardProps) {
  const { data: completionRes } = useProfileCompletion(
    profileId,
    Boolean(profileId),
  );

  const completion = completionRes?.data;
  const overallCompletion = completion?.overall_completion ?? 0;
  const sections = completion?.sections;

  const completionData = [
    { name: "Completion", value: overallCompletion, fill: "#6B21A8" },
  ];

  const sectionItems = [
    {
      label: "Personal",
      val: sections?.personal?.percentage ?? 0,
      color: "bg-purple-500",
    },
    {
      label: "Experience",
      val: sections?.professional?.percentage ?? 0,
      color: "bg-emerald-500",
    },
    {
      label: "Skills",
      val: sections?.skills?.percentage ?? 0,
      color: "bg-amber-500",
    },
    {
      label: "Projects",
      val: sections?.projects?.percentage ?? 0,
      color: "bg-rose-500",
    },
    {
      label: "Education",
      val: sections?.education?.percentage ?? 0,
      color: "bg-orange-500",
    },
    {
      label: "Certifications",
      val: sections?.certifications?.percentage ?? 0,
      color: "bg-blue-500",
    },
  ];

  return (
    <div
      className={`bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-800 ${className}`}
    >
      <h2 className="text-sm font-bold text-purple-900 dark:text-purple-300 mb-6">
        {title}
      </h2>

      <div className="flex items-center gap-6 mb-6">
        <div className="w-28 h-28 relative shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="70%"
              outerRadius="100%"
              barSize={8}
              data={completionData}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                background={{ fill: "#F1F5F9" }}
                dataKey="value"
                cornerRadius={4}
              />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xl font-bold text-slate-900 dark:text-white">
              {overallCompletion}%
            </span>
            <span className="text-[9px] font-medium text-slate-500 uppercase">
              Complete
            </span>
          </div>
        </div>

        <div className="flex-1 space-y-2.5">
          {sectionItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center justify-between text-[10px]"
            >
              <div className="flex items-center gap-2">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${item.color}`}
                ></span>
                <span className="text-slate-600 dark:text-slate-400 font-medium">
                  {item.label}
                </span>
              </div>
              <span className="font-bold text-slate-900 dark:text-white">
                {item.val}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* {showViewDetails && (
        <Button
          variant="link"
          className="text-xs font-semibold text-purple-700 hover:text-purple-800 p-0 h-auto"
        >
          View Details <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )} */}
    </div>
  );
}
